import { useState, type MouseEvent as ReactMouseEvent } from "react";
import type { DataState, ForecastPeriod, HourlyPeriod, TemperatureUnit, WeatherAlert, WeatherSummary } from "../../../shared/models";
import { compassPoint, convertTemp, formatTemp, kphToMph } from "../../../shared/weather";
import { useAppUi } from "../../app/uiContext";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf, useWeather } from "../../data/queries";
import { Icon } from "../../ui/Icon";
import { Sheet } from "../../ui/Sheet";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { relativeAge, useNow } from "../../ui/time";
import { WeatherIcon } from "../../ui/WeatherIcon";
import type { WidgetDefinition, WidgetProps } from "../types";
import { remPx, useBoxSize } from "../sky/common";
import { WidgetHeader } from "../WidgetHeader";

type Display = "greeting" | "strip" | "conditions" | "week" | "detailed";

interface WeatherConfig {
  display: Display;
}

/** Shared data hook: every weather/radar widget reads the same cached summary. */
export function useWeatherSummary() {
  const { settings } = useHousehold();
  const q = useWeather(Boolean(settings.location));
  return { q, state: dataStateOf(q, 30 * 60_000), hasLocation: Boolean(settings.location), settings };
}

export function fcTemp(p: { temperature: number; temperatureUnit: TemperatureUnit }, unit: TemperatureUnit) {
  return `${Math.round(convertTemp(p.temperature, p.temperatureUnit, unit))}°`;
}

/** Today's high/low from the forecast periods (the first may already be "Tonight"). */
export function highLow(daily: ForecastPeriod[], unit: TemperatureUnit): { high: string | null; low: string | null } {
  const first = daily[0];
  if (!first) return { high: null, low: null };
  if (first.isDaytime) {
    const night = daily[1];
    return { high: fcTemp(first, unit), low: night && !night.isDaytime ? fcTemp(night, unit) : null };
  }
  return { high: null, low: fcTemp(first, unit) };
}

export function windText(kph: number | null, deg: number | null, unit: TemperatureUnit): string | null {
  if (kph == null) return null;
  if (kph < 1) return "Calm";
  return `${compassPoint(deg)} ${unit === "F" ? `${Math.round(kphToMph(kph))} mph` : `${Math.round(kph)} km/h`}`;
}

export function greetingFor(hour: number): string {
  if (hour < 5) return "Good night";
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

interface DayRow {
  date: string;
  name: string;
  icon: ForecastPeriod["icon"];
  high: number | null;
  low: number | null;
  pop: number | null;
}

/** Pair NWS day/night periods into calendar days. */
function toDays(daily: ForecastPeriod[], unit: TemperatureUnit, timeZone: string): DayRow[] {
  const byDate = new Map<string, DayRow>();
  for (const p of daily) {
    const date = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(p.startTime));
    const row = byDate.get(date) ?? { date, name: "", icon: p.icon, high: null, low: null, pop: null };
    const t = Math.round(convertTemp(p.temperature, p.temperatureUnit, unit));
    if (p.isDaytime) {
      row.high = t;
      row.icon = p.icon;
      row.name = p.name;
    } else {
      row.low = t;
      if (!row.name) row.name = p.name;
    }
    row.pop = Math.max(row.pop ?? 0, p.precipChancePct ?? 0) || null;
    byDate.set(date, row);
  }
  const days = [...byDate.values()];
  days.forEach((d, i) => {
    d.name = i === 0 ? "Today" : new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short" }).format(new Date(`${d.date}T12:00:00Z`));
  });
  return days;
}

/** The whole card opens weather, except its own buttons and links and anything in a sheet it opened. */
function tapToOpen(open: () => void) {
  return (e: ReactMouseEvent<HTMLElement>) => {
    const target = e.target as Element;
    if (!e.currentTarget.contains(target) || target.closest("button, a")) return;
    open();
  };
}

function Weather({ config, size, editing, openFullscreen, fullscreen }: WidgetProps<WeatherConfig>) {
  const { q, state, hasLocation, settings } = useWeatherSummary();
  const { openSettings, openScreen } = useAppUi();
  const { isAdmin, timeZone } = useHousehold();
  useNow("minute");

  if (!hasLocation) {
    return (
      <EmptyState
        icon="pin"
        title="Where’s home?"
        detail={size.variant === "compact" ? undefined : "Set a general location to see local weather."}
        action={
          isAdmin ? (
            <button className="btn btn-sm btn-primary" onClick={() => openSettings("location")}>
              Set location
            </button>
          ) : undefined
        }
      />
    );
  }
  const data = q.data;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="cloud" title="Weather unavailable" detail={q.error?.message} />;

  const unit = settings.temperatureUnit;
  const display: Display = fullscreen ? "detailed" : config.display;
  const common = { data, unit, state, updatedAt: q.dataUpdatedAt, timeZone };
  // Tapping the weather goes to the Weather & sky screen; on that screen (or without one), the full-screen card.
  const open = () => {
    if (!openScreen("weather-focus")) openFullscreen();
  };
  const onCard = editing || fullscreen ? undefined : tapToOpen(open);

  switch (display) {
    case "greeting":
      return <Greeting {...common} onOpen={open} />;
    case "strip":
      return <Strip {...common} onOpen={open} />;
    case "week":
      return (
        <div className={`wx ${onCard ? "wx-tappable" : ""}`} onClick={onCard}>
          <WidgetHeader title="This week" onTitle={open} meta={<Provenance data={data} short />} />
          <WeekRows days={toDays(data.daily, unit, timeZone)} max={7} />
          <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
        </div>
      );
    default:
      return <Panel {...common} detailed={display === "detailed"} maxAge={settings.observationMaxAgeMinutes} height={size.height} onOpen={open} onCard={onCard} />;
  }
}

interface ViewProps {
  data: WeatherSummary;
  unit: TemperatureUnit;
  state: DataState;
  updatedAt: number;
  timeZone: string;
}

function Greeting({ data, unit, timeZone, onOpen }: ViewProps & { onOpen: () => void }) {
  const now = useNow("minute");
  const hour = Number(new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", hour12: false }).format(now));
  const obs = data.observation;
  const fc = data.hourly[0];
  const { high } = highLow(data.daily, unit);
  const desc = (obs?.description ?? fc?.shortForecast)?.toLowerCase();
  return (
    <button className="greeting" onClick={onOpen} aria-label="Open weather details">
      <WeatherIcon kind={obs?.icon ?? data.hourly[0]?.icon ?? "unknown"} isDaytime={obs?.isDaytime ?? true} size="3.4rem" />
      <span className="greeting-text">
        <span className="greeting-hello">{greetingFor(hour)}</span>
        <span className="greeting-sub">
          {obs ? `${formatTemp(obs.temperatureC, unit)}${desc ? ` and ${desc}` : ""}` : fc ? `${desc ? desc[0]!.toUpperCase() + desc.slice(1) : "Forecast"}` : "Here’s what’s ahead today"}
          {high && ` · high of ${high}`}
        </span>
        {data.alerts[0] && (
          <span className="greeting-alert">
            <Icon name="alert" size="1rem" /> {data.alerts[0].event}
          </span>
        )}
      </span>
    </button>
  );
}

function Strip({ data, unit, state, updatedAt, onOpen }: ViewProps & { onOpen: () => void }) {
  const obs = data.observation;
  const { high, low } = highLow(data.daily, unit);
  const current = data.hourly[0];
  return (
    <button className="wx-strip" onClick={onOpen} aria-label="Open weather details">
      <WeatherIcon kind={obs?.icon ?? current?.icon ?? "unknown"} isDaytime={obs?.isDaytime ?? current?.isDaytime} size="3rem" />
      <span className="wx-strip-temp">{obs ? formatTemp(obs.temperatureC, unit) : current ? fcTemp(current, unit) : "—"}</span>
      <span className="wx-strip-meta">
        <span>{obs?.description ?? current?.shortForecast ?? ""}</span>
        <span className="muted">
          {high && `H ${high}`} {low && `L ${low}`}
        </span>
      </span>
      {data.alerts.length > 0 && <Icon name="alert" className="wx-alert-dot" />}
      <StatusBadge state={state} updatedAt={updatedAt} />
    </button>
  );
}

export function CurrentRow({ data, unit, big }: { data: WeatherSummary; unit: TemperatureUnit; big?: boolean }) {
  const obs = data.observation;
  // No fresh station reading: show this hour's forecast instead, labelled as a forecast.
  const fc = obs ? null : data.hourly[0];
  const { high, low } = highLow(data.daily, unit);
  const pop = data.hourly[0]?.precipChancePct;
  const wind = obs ? windText(obs.windSpeedKph, obs.windDirectionDeg, unit) : null;
  return (
    <div className={`wx-now ${big ? "big" : ""}`}>
      <WeatherIcon kind={obs?.icon ?? fc?.icon ?? "unknown"} isDaytime={obs?.isDaytime ?? fc?.isDaytime} size={big ? "4.6rem" : "3.6rem"} />
      <div className={`wx-now-temp ${fc ? "is-forecast" : ""}`}>{obs ? formatTemp(obs.temperatureC, unit) : fc ? fcTemp(fc, unit) : "—"}</div>
      <div className="wx-now-desc">
        <strong>{obs?.description ?? fc?.shortForecast ?? "No current reading"}</strong>
        {obs?.feelsLikeC != null && Math.abs(obs.feelsLikeC - obs.temperatureC) >= 1 && <span>Feels like {formatTemp(obs.feelsLikeC, unit)}</span>}
        {fc && <span className="forecast-tag">Forecast · stations not reporting</span>}
      </div>
      <dl className="wx-facts">
        {(high || low) && (
          <div className="wx-fact">
            <dt className="sr-only">High and low</dt>
            <dd>
              {high && <>H: {high}</>}
              {high && low && " "}
              {low && <>L: {low}</>}
            </dd>
          </div>
        )}
        {pop != null && (
          <div className="wx-fact">
            <dt>
              <Icon name="umbrella" size="1rem" />
              <span className="sr-only">Chance of rain</span>
            </dt>
            <dd>{pop}%</dd>
          </div>
        )}
        {wind && (
          <div className="wx-fact">
            <dt>
              <Icon name="wind" size="1rem" />
              <span className="sr-only">Wind</span>
            </dt>
            <dd>{wind}</dd>
          </div>
        )}
        {obs?.humidityPct != null && (
          <div className="wx-fact">
            <dt>
              <Icon name="drop" size="1rem" />
              <span className="sr-only">Humidity</span>
            </dt>
            <dd>{Math.round(obs.humidityPct)}%</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

function Panel({
  data,
  unit,
  state,
  updatedAt,
  timeZone,
  detailed,
  maxAge,
  height,
  onOpen,
  onCard,
}: ViewProps & { detailed: boolean; maxAge: number; height: number; onOpen: () => void; onCard?: (e: ReactMouseEvent<HTMLElement>) => void }) {
  const days = toDays(data.daily, unit, timeZone);
  // rem-based: the text (and so every row) grows with the screen.
  const showHourly = detailed || height > remPx() * 24;
  const obs = data.observation;
  const stale = obs ? (Date.now() - Date.parse(obs.observedAt)) / 60_000 > maxAge : false;
  return (
    <div className={`wx ${onCard && !detailed ? "wx-tappable" : ""}`} onClick={detailed ? undefined : onCard}>
      <WidgetHeader title={data.location.label || "Weather"} onTitle={detailed ? undefined : onOpen} meta={<Provenance data={data} />} />
      {data.alerts.length > 0 && <AlertBanner alerts={data.alerts} />}
      <CurrentRow data={data} unit={unit} big={detailed} />
      {showHourly && data.hourly.length > 0 && <Hourly hours={data.hourly.slice(0, detailed ? 24 : 12)} unit={unit} timeZone={data.location.timeZone} />}
      <WeekRows days={days} max={7} />
      <footer className={`wx-foot ${stale ? "warn-text" : ""}`}>
        {obs ? (
          <span>
            Observed {relativeAge(obs.observedAt)} at {obs.station.name} ({obs.station.id}), {unit === "F" ? `${Math.max(1, Math.round(obs.station.distanceKm * 0.621))} mi` : `${Math.max(1, Math.round(obs.station.distanceKm))} km`} away
          </span>
        ) : (
          <span className="warn-text">{data.observationNote}</span>
        )}
        <StatusBadge state={state} updatedAt={updatedAt} />
      </footer>
    </div>
  );
}

function Provenance({ data, short }: { data: WeatherSummary; short?: boolean }) {
  const f = data.stamps.forecast;
  if (short) return <>NWS forecast{f?.servedFromCache ? ` · saved ${relativeAge(f.fetchedAt)}` : ""}</>;
  const obs = data.observation;
  return (
    <>
      <Icon name="navigate" size="0.85rem" /> {obs ? `Updated ${new Date(obs.observedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: data.location.timeZone })}` : "No recent observation"}
    </>
  );
}

function Hourly({ hours, unit, timeZone }: { hours: HourlyPeriod[]; unit: TemperatureUnit; timeZone: string }) {
  // Only whole hours that fit the card's width; a half-hidden last hour reads as cut off.
  const [ref, box] = useBoxSize<HTMLDivElement>();
  const rem = remPx();
  const fit = box.width ? Math.max(1, Math.floor((box.width + rem * 0.2) / (rem * 3.6))) : hours.length;
  return (
    <div className="wx-hourly" aria-label="Hourly forecast" ref={ref}>
      {hours.slice(0, fit).map((h, i) => (
        <div key={h.startTime} className="wx-hour">
          <span className="muted">{i === 0 ? "Now" : new Date(h.startTime).toLocaleTimeString("en-US", { hour: "numeric", timeZone })}</span>
          <WeatherIcon kind={h.icon} isDaytime={h.isDaytime} size="2rem" />
          <strong>{fcTemp(h, unit)}</strong>
          <span className="wx-pop">{h.precipChancePct ? `${h.precipChancePct}%` : " "}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Day rows with a low→high range bar scaled across the week. The list takes
 * the card's remaining height and shows only whole rows that fit it (rows
 * are sized in rem, so the count depends on the screen, not a pixel guess).
 */
function WeekRows({ days, max }: { days: DayRow[]; max: number }) {
  const [ref, box] = useBoxSize<HTMLUListElement>();
  // The list stretches to the free space even when empty, so measuring it
  // before any rows are drawn is fine (0 rows when there is no room).
  const fit = Math.floor(box.height / (remPx() * 2.75));
  const shown = days.slice(0, Math.min(max, fit));
  const temps = shown.flatMap((d) => [d.high, d.low]).filter((t): t is number => t != null);
  const lo = Math.min(...temps);
  const hi = Math.max(...temps);
  const span = Math.max(1, hi - lo);
  return (
    <ul className="wx-week" ref={ref}>
      {shown.map((d) => {
        const a = d.low ?? d.high ?? lo;
        const b = d.high ?? d.low ?? hi;
        return (
          <li key={d.date}>
            <span className="wx-day">{d.name}</span>
            <WeatherIcon kind={d.icon} size="1.9rem" />
            <span className="wx-pop">{d.pop ? `${d.pop}%` : ""}</span>
            <span className="wx-lo">{d.low != null ? `${d.low}°` : ""}</span>
            <span className="wx-range" aria-hidden="true">
              <i style={{ left: `${((a - lo) / span) * 100}%`, right: `${100 - ((b - lo) / span) * 100}%` }} />
            </span>
            <span className="wx-hi">{d.high != null ? `${d.high}°` : ""}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function AlertBanner({ alerts }: { alerts: WeatherAlert[] }) {
  const [open, setOpen] = useState(false);
  const first = alerts[0]!;
  const severe = ["Extreme", "Severe"].includes(first.severity);
  return (
    <>
      <button className={`wx-alert ${severe ? "severe" : ""}`} onClick={() => setOpen(true)}>
        <Icon name="alert" size="1.1rem" />
        <span>
          <strong>{first.event}</strong>
          {first.ends && ` until ${new Date(first.ends).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" })}`}
          {alerts.length > 1 && ` · +${alerts.length - 1} more`}
        </span>
      </button>
      {open && (
        <Sheet title="Weather alerts" onClose={() => setOpen(false)} size="lg">
          {alerts.map((a) => (
            <article key={a.id} className="alert-detail">
              <h3>{a.event}</h3>
              <p className="muted">
                {a.senderName} · {a.severity}
                {a.effective && ` · from ${new Date(a.effective).toLocaleString()}`}
                {a.ends && ` until ${new Date(a.ends).toLocaleString()}`}
              </p>
              {a.headline && <p>{a.headline}</p>}
              <p className="pre">{a.description}</p>
              {a.instruction && (
                <p className="pre">
                  <strong>What to do: </strong>
                  {a.instruction}
                </p>
              )}
            </article>
          ))}
          <p className="muted small">Source: National Weather Service active alerts.</p>
        </Sheet>
      )}
    </>
  );
}

export const weatherWidget: WidgetDefinition<WeatherConfig> = {
  type: "weather",
  version: 1,
  name: "Weather",
  description: "Current conditions from the nearest reporting station, plus the NWS forecast and alerts.",
  icon: "cloud",
  category: "weather",
  defaultSize: { w: 4, h: 4 },
  minSize: { w: 2, h: 1 },
  stackHeight: (c) => (c.display === "greeting" || c.display === "strip" ? 96 : c.display === "detailed" ? 640 : c.display === "week" ? 380 : 420),
  defaults: { display: "conditions" },
  settings: [
    {
      key: "display",
      label: "Display",
      type: "select",
      options: [
        { value: "greeting", label: "Greeting" },
        { value: "strip", label: "Compact strip" },
        { value: "conditions", label: "Current conditions" },
        { value: "week", label: "This week" },
        { value: "detailed", label: "Detailed forecast" },
      ],
    },
  ],
  provider: "nws",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  chrome: (c) => (c.display === "greeting" ? "bare" : "card"),
  Component: Weather,
};
